/* BookCoverPanel — per-book cover image manager.
 * Opened from the admin Books list via the ImageIcon button on each row.
 *
 * Two entry paths:
 *  1. Drag-and-drop / click to upload an image file
 *     → POST /admin/bookstore/books/{id}/cover-image  (multipart)
 *  2. Paste an external image URL
 *     → PUT  /admin/bookstore/books/{id}  { cover_front_url }
 *
 * Also exposes the existing AI "Regenerate" button so the admin can
 * trigger a fresh Nano Banana cover without leaving this panel.
 */
import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Upload, ImageIcon, Sparkles, X, Link2, ExternalLink } from 'lucide-react';
import { bookstoreAdminApi, resolveCoverUrl } from '@/lib/bookstoreApi';

const ACCEPT   = 'image/jpeg,image/png,image/webp,image/gif,image/avif';
const MAX_MB   = 20;
// Canva's book-cover template gallery — 1410×2250 px, no login required to browse
const CANVA_URL = 'https://www.canva.com/create/book-covers/';

export default function BookCoverPanel({ book, onClose, onUpdated }) {
  const [coverUrl, setCoverUrl]   = useState(book.cover_front_url || '');
  const [urlInput, setUrlInput]   = useState(book.cover_front_url || '');
  const [busy,     setBusy]       = useState(false);
  const [dragOver, setDragOver]   = useState(false);
  const inputRef                  = useRef(null);

  /* ── file upload ─────────────────────────────────────────────────── */
  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Pick an image file.'); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`Image must be ≤ ${MAX_MB} MB.`); return; }
    setBusy(true);
    try {
      const r = await bookstoreAdminApi.uploadBookCover(book.id, file);
      setCoverUrl(r.cover_front_url);
      setUrlInput(r.cover_front_url);
      toast.success('Cover uploaded.');
      onUpdated?.({ ...book, cover_front_url: r.cover_front_url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally { setBusy(false); }
  }, [book, onUpdated]);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    uploadFile(e.dataTransfer?.files?.[0]);
  };

  /* ── URL save ────────────────────────────────────────────────────── */
  const saveUrl = async () => {
    const url = urlInput.trim();
    if (!url) return;
    setBusy(true);
    try {
      await bookstoreAdminApi.updateBook(book.id, { cover_front_url: url });
      setCoverUrl(url);
      toast.success('Cover URL saved.');
      onUpdated?.({ ...book, cover_front_url: url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  /* ── AI regenerate ───────────────────────────────────────────────── */
  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await bookstoreAdminApi.regenerateBookCover(book.id);
      setCoverUrl(r.cover_front_url);
      setUrlInput(r.cover_front_url);
      toast.success('New AI cover generated.');
      onUpdated?.({ ...book, cover_front_url: r.cover_front_url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI generation failed.');
    } finally { setBusy(false); }
  };

  /* ── render ──────────────────────────────────────────────────────── */
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-md rounded-xl bg-background shadow-2xl ring-1 ring-border overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <p className="text-[13px] font-semibold text-foreground">Book Cover</p>
            <p className="text-[11px] text-muted-foreground truncate max-w-[280px]">{book.title}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-secondary transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Current cover preview */}
          <div className="flex justify-center">
            <div className="relative w-36 rounded-md overflow-hidden border border-border shadow-md aspect-[2/3] bg-secondary">
              {coverUrl ? (
                <img
                  src={resolveCoverUrl(coverUrl)}
                  alt="Current cover"
                  className="w-full h-full object-cover"
                  data-testid="book-cover-preview"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
                  <ImageIcon className="h-8 w-8 opacity-40" />
                  <span className="text-[10px]">No cover yet</span>
                </div>
              )}
            </div>
          </div>

          {/* Design in Canva — opens Canva's book-cover template gallery in a new tab */}
          <a
            href={CANVA_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="book-cover-canva-link"
            className="flex items-center justify-center gap-2 rounded-lg border-2 border-[#7d2ae8]/40
                       bg-[#7d2ae8]/5 px-4 py-3 text-[12px] font-semibold text-[#7d2ae8]
                       hover:bg-[#7d2ae8]/10 hover:border-[#7d2ae8]/60 transition-colors"
          >
            {/* Canva wordmark-colour icon */}
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-[#7d2ae8]" aria-hidden="true">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.018 14.395c-.32.77-.86 1.374-1.56 1.77-.71.4-1.52.574-2.41.512a4.29 4.29 0 0 1-2.29-.795 4.408 4.408 0 0 1-1.463-1.926 4.633 4.633 0 0 1-.26-2.438 4.503 4.503 0 0 1 .96-2.187 4.39 4.39 0 0 1 1.851-1.307 4.273 4.273 0 0 1 2.27-.166c.562.105 1.086.337 1.548.683.32.237.396.69.169 1.019a.72.72 0 0 1-1.008.17 2.86 2.86 0 0 0-1.02-.448 2.83 2.83 0 0 0-1.494.11 2.898 2.898 0 0 0-1.222.862 2.978 2.978 0 0 0-.63 1.443 3.075 3.075 0 0 0 .17 1.61 2.927 2.927 0 0 0 .97 1.277c.44.322.96.52 1.51.536.535.016 1.068-.142 1.523-.453.456-.31.802-.757.989-1.277a.72.72 0 0 1 .928-.444.727.727 0 0 1 .449.93z"/>
            </svg>
            Design in Canva
            <ExternalLink className="h-3 w-3 opacity-60" />
          </a>
          <p className="text-center text-[10px] text-muted-foreground -mt-3">
            Opens Canva's book cover templates (1410×2250 px). Export your design, then paste the URL or upload the file above.
          </p>

          {/* Drop zone */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => !busy && inputRef.current?.click()}
            onKeyDown={(e) => e.key === 'Enter' && !busy && inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            data-testid="book-cover-dropzone"
            className={`flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-5 text-center cursor-pointer transition-colors ${
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50 hover:bg-secondary/40'
            } ${busy ? 'pointer-events-none opacity-60' : ''}`}
          >
            {busy ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <Upload className="h-6 w-6 text-muted-foreground" />
            )}
            <span className="text-[12px] text-muted-foreground">
              {busy ? 'Uploading…' : 'Drop a cover image here, or click to browse'}
            </span>
            <span className="text-[10px] text-muted-foreground/60">JPEG, PNG, WebP · up to {MAX_MB} MB</span>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              data-testid="book-cover-file-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }}
            />
          </div>

          {/* URL paste */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <Link2 className="h-3 w-3" /> Or paste an image URL
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://…"
                disabled={busy}
                data-testid="book-cover-url-input"
                className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-[12px]
                           placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2
                           focus:ring-ring disabled:opacity-60"
              />
              <button
                type="button"
                onClick={saveUrl}
                disabled={busy || !urlInput.trim() || urlInput.trim() === (book.cover_front_url || '')}
                data-testid="book-cover-url-save"
                className="rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold
                           text-primary-foreground hover:bg-primary/90 disabled:opacity-50
                           transition-colors"
              >
                Save
              </button>
            </div>
          </div>

          {/* AI Regenerate */}
          <div className="border-t border-border pt-4">
            <button
              type="button"
              onClick={regenerate}
              disabled={busy}
              data-testid="book-cover-ai-regen"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md
                         border border-border px-4 py-2 text-[12px] font-medium
                         hover:bg-secondary transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Generate with AI (Nano Banana)
            </button>
            <p className="mt-1 text-[10px] text-center text-muted-foreground">
              Uses the book's metadata &amp; genre to create a new cover. Takes ~10–15 s.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
